import * as React from 'react'
import {ReactElement, ReactNode} from 'react'
import {BlockSpacer, currentMedia} from '../../../styledComponents/ResponsiveConvenience'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet
} from '../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import styled from 'styled-components'
import {Box, CardActionArea} from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import {PlainBackLink, PlainLink} from '../../../styledComponents/Links'
import {DisplayItemDividerForPageTop} from '../../../styledComponents/DisplayItem'
import {DISASTER_TOPIC_CATEGORY} from '../../../CodeMaster'
import ScrollableCategorySelector from '../../../components/shared/ScrollableCategorySelector'
import {Colors, FontSize, FontWeights} from '../../../styledComponents/Constants'
import {DisasterTopicInfo} from './repository/NatGetDisasterTopicCategory'
import {Nullable} from '../../../app/TypeHelpers'
import NatDisasterRepo from './repository/NatDisasterRepo'
import {StyledDiv} from '../../../styledComponents/StyledTypeDefinitions'
import moment, {Moment} from 'moment/moment'
import {FontAwesomeIconWrapper, UserIcon} from '../../../components/assocDirector/AssocDirectorCommon'
import {RowFlexContainer} from '../../../styledComponents/FlexComponents'
import {faCommentDots} from '@fortawesome/free-regular-svg-icons'
import NatCurrentDateTimeRepo from '../currentDateTime/repository/NatCurrentDateTimeRepo'
import {NatDisasterHelper} from './common/NatDisasterHelper'
import {StyledPageTitle} from '../../../components/reservations/components/CommonStyle'
import AuthorityChecker from '../../../components/AuthorityChecker'
import {HKConstants} from '../../../HKConstants'
import {formatDate} from '../../../formatters/formatDate'
import {SessionStorageHelper} from '../../../SessionStorageHelper'
import {Location} from 'react-router-dom'

const KEY_DISASTER_TOPIC_SELECTED_CATEGORYLIST = 'NatResidentDisasterTopicListPage.selectedCategoryList'
const KEY_PAST_DISASTER_TOPIC_SELECTED_CATEGORYLIST = 'NatResidentDisasterTopicListPage.pastSelectedCategoryList'
import {faTrash} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

interface Props {
    condominiumCd: string,
    disasterRepo: NatDisasterRepo,
    currentDateTimeRepo: NatCurrentDateTimeRepo,
    pastFlag?: boolean,
    location: Location
}

interface State {
    disasterTopicList: DisasterTopicInfo[]
    disasterCategory: DISASTER_TOPIC_CATEGORY[]
    selectedCategory: DISASTER_TOPIC_CATEGORY[]
    currentDateTime: Moment
    prevDisasterEndDate: Nullable<string>
    isLoadCompleted: boolean,
}

class NatResidentDisasterTopicListPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            disasterTopicList: [],
            disasterCategory: DISASTER_TOPIC_CATEGORY.values(),
            selectedCategory: [],
            currentDateTime: moment(),
            prevDisasterEndDate: null,
            isLoadCompleted: false
        }

        this.handleChangeCategory = this.handleChangeCategory.bind(this)
        this.onClickRegisterTopic = this.onClickRegisterTopic.bind(this)
        this.onClickTopic = this.onClickTopic.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter} = this.props

        await eventEmitter.showLoading()
        try {
            const [currentDateTime, disasterTopic, prevDisasterEndDate] = await Promise.all([
                this.getCurrentDateTime(),
                this.getDisasterTopicList(),
                this.getPrevDisasterEndDate()
            ])

            if (currentDateTime && disasterTopic) {
                this.setState({
                    currentDateTime: moment(currentDateTime),
                    disasterTopicList: disasterTopic,
                    prevDisasterEndDate: prevDisasterEndDate,
                    selectedCategory: this.getSelectedCategoryList(DISASTER_TOPIC_CATEGORY.values())
                })
            }
        } finally {
            this.setState({isLoadCompleted: true})
            eventEmitter.closeLoading()
        }
    }

    async getCurrentDateTime(): Promise<Nullable<string>> {
        const {currentDateTimeRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details?.currentDateTime ?? null
    }

    async getDisasterTopicList(): Promise<Nullable<DisasterTopicInfo[]>> {
        const {condominiumCd, disasterRepo, eventEmitter, pastFlag} = this.props

        const response = await this.props.asyncCallFunction(
            disasterRepo.getDisasterTopic.bind(disasterRepo, condominiumCd, pastFlag),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details?.topicInfo ?? null
    }

    async getPrevDisasterEndDate(): Promise<Nullable<string>> {
        const {condominiumCd, disasterRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            disasterRepo.getDisasterMode.bind(disasterRepo, condominiumCd),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.prevDisasterEndDate ?? null
    }

    setSelectedCategoryList(selectedCategory: DISASTER_TOPIC_CATEGORY[]): void {
        // セッションストレージに、選択した（されている）絞り込みデータをセット
        const selectedCategories = selectedCategory.map(category => {
            return category.value
        })
        SessionStorageHelper.setItem(this.props.pastFlag ?
                KEY_PAST_DISASTER_TOPIC_SELECTED_CATEGORYLIST : KEY_DISASTER_TOPIC_SELECTED_CATEGORYLIST,
            JSON.stringify(selectedCategories)
        )
    }

    getSelectedCategoryList(disasterCategory: DISASTER_TOPIC_CATEGORY[]): DISASTER_TOPIC_CATEGORY[]{
        const json = SessionStorageHelper.getStringItemOrNull(this.props.pastFlag ?
            KEY_PAST_DISASTER_TOPIC_SELECTED_CATEGORYLIST : KEY_DISASTER_TOPIC_SELECTED_CATEGORYLIST)
        if (json) {
            const selectedCategoryFromSession: string[] = JSON.parse(json)
            return disasterCategory
                .filter(category => selectedCategoryFromSession.includes(category.value))
        }
        return []
    }

    handleChangeCategory(changeCategoryList: DISASTER_TOPIC_CATEGORY[]): void {
        this.setSelectedCategoryList(changeCategoryList)
        this.setState({
            selectedCategory:changeCategoryList
        })
    }

    private filterTopicList(
        allTopicList: DisasterTopicInfo[],
        topicDivisions: DISASTER_TOPIC_CATEGORY[]
    ): DisasterTopicInfo[] {
        if (topicDivisions.length === 0) {
            return allTopicList
        }

        const topicDivisionsList: string[] = topicDivisions
            .map(category => category.value)

        return allTopicList
            .filter(topic => topicDivisionsList.includes(topic.category))
    }

    onClickRegisterTopic(): void {
        const {condominiumCd} = this.props
        this.props.navigate(`/resident/condominiums/${condominiumCd}/disasters/topics/register`)
    }

    onClickTopic(topicId: string): void {
        const {condominiumCd, pastFlag} = this.props
        let topicUrl = `/resident/condominiums/${condominiumCd}/disasters/topics/${topicId}`

        if (pastFlag) {
            topicUrl += `/past`
        }
        this.props.navigate(topicUrl)
    }

    renderOverview(): ReactNode {
        const {prevDisasterEndDate} = this.state
        let disasterEndDate: string = '';

        if (prevDisasterEndDate !== null) {
            disasterEndDate = formatDate(prevDisasterEndDate);
        }

        return <Box>
            {this.props.pastFlag ?
                <>
                    前回の災害時モード終了日（{disasterEndDate}）からさかのぼって3年間分の情報共有内容を確認できます。
                </>
                :
                <>
                    このマンションの居住者同士で情報共有を行うことができます。<br/>
                    新規トピックは管理組合役員のみ登録することができます。
                </>
            }
            {prevDisasterEndDate &&
                <Box textAlign={'right'} mt={'15px'}>
                    {(!this.props.pastFlag) &&
                        <HistoryLink
                            to={{
                                pathname: `/resident/condominiums/${this.props.condominiumCd}/disasters/topics/past`
                            }}>
                            過去のトピックを見る
                        </HistoryLink>
                    }
                </Box>
            }
            {!this.props.pastFlag &&
                <Box height={'10px'}/>
            }
        </Box>
    }

    renderTopicList(): ReactNode {
        const {disasterTopicList, selectedCategory} = this.state

        const viewTopicList = this.filterTopicList(disasterTopicList, selectedCategory)

        return <>
            {(!this.props.pastFlag) &&
                <TopicArea>
                    <AuthorityChecker
                        permittedAuthorities={HKConstants.CMN.AUTH.REGISTER_TOPIC_DISASTER_INFORMATION_SHARING}>
                        <StyledCardActionArea onClick={this.onClickRegisterTopic}>
                            <CreateTopicContainer>
                                <CreateTopicIcon src={`/resources/images/icons/icon_topic_registration.svg`}
                                                 alt={'トピック登録'}/>
                                トピック登録
                            </CreateTopicContainer>
                        </StyledCardActionArea>
                    </AuthorityChecker>
                </TopicArea>
            }
            {viewTopicList.length === 0 ?
                <Box mt={'20px'}>トピックはありません</Box> :
                <TopicArea>
                    {viewTopicList.map((topicInfo, index) => (
                        <React.Fragment key={index}>
                            {this.renderTopic(topicInfo)}
                        </React.Fragment>

                    ))}
                </TopicArea>
            }
        </>
    }


    renderTopicCategory(): ReactNode {
        return <Box mb={'10px'}>
            <ScrollableCategorySelector
                onChangeCategory={this.handleChangeCategory}
                categoryList={this.state.disasterCategory}
                selectedCategoryList={this.state.selectedCategory}
                displayAllButton
                newDesign
                noScrolling
            />
        </Box>
    }

    renderTopic(topicInfo: DisasterTopicInfo): ReactNode {
        const {currentDateTime} = this.state
        const {topicId, title, category, commentCount, updaterRoleId, updateDatetime, latestComment} = topicInfo
        const {pastFlag} = this.props

        return <StyledCardActionArea onClick={() => this.onClickTopic(topicId)}>
            <TopicContainer>
                <TopicIndex pastFlag={pastFlag}/>
                <Box width={'100%'} pl={'10px'} minWidth={'0'}>
                    <TopicHeader>
                        <CategoryLabel>
                            {NatDisasterHelper.getTopicCategoryLabel(category)}
                        </CategoryLabel>
                        <Box pl={'5px'}>
                            {NatDisasterHelper.getTopicCreator(updaterRoleId)}
                        </Box>
                        <Box textAlign={'right'} flexGrow={1}>
                            {NatDisasterHelper.formatDoNotDisplayCurrentYear(currentDateTime, moment(updateDatetime))}
                        </Box>
                    </TopicHeader>
                    <TopicTitle>{title}</TopicTitle>
                    <CommentCountContainer>
                        <RowFlexContainer>
                            <FontAwesomeIconWrapper
                                icon={faCommentDots}
                                iconSize={'20px'}
                                iconColor={Colors.AccentDark}
                            />
                            <Box ml={'5px'}>{commentCount ? commentCount : 0}</Box>
                        </RowFlexContainer>
                    </CommentCountContainer>
                    {latestComment && (
                        <>
                            {latestComment.deleterRoleId &&
                                <TopicCommentContainer>
                                    <IconWithCircleStyle>
                                        <FontAwesomeIcon icon={faTrash}/>
                                    </IconWithCircleStyle>
                                    <TopicComment>
                                        {NatDisasterHelper.getDeleteMessage(
                                            latestComment.deleterRoleId, latestComment.isDeleterSelf)}
                                    </TopicComment>
                                </TopicCommentContainer>
                            }
                            {!latestComment.deleterRoleId &&
                                <TopicCommentContainer>
                                    <UserIcon iconSize={'20px'}/>
                                    <TopicComment>{latestComment.comment}</TopicComment>
                                    <Box textAlign={'right'} flexGrow={'1'}>
                                        {NatDisasterHelper
                                            .getPostDatetime(currentDateTime, moment(latestComment.postDatetime))}
                                    </Box>
                                </TopicCommentContainer>
                            }
                        </>
                    )}
                </Box>
            </TopicContainer>
        </StyledCardActionArea>
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }
        const {pastFlag} = this.props
        let plainBackUrl: string = ''
        let pageTitle: string = ''

        if (pastFlag) {
            plainBackUrl = `/resident/condominiums/${this.props.condominiumCd}/disasters/topics`
            pageTitle = '過去のトピック'
        } else {
            plainBackUrl = `/resident/condominiums/${this.props.condominiumCd}/disasters/prevention/top`
            pageTitle = '災害時情報共有'
        }

        return (
            <>
                <PlainBackLink
                    to={plainBackUrl}
                >
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <StyledPageTitle>
                    {pageTitle}
                </StyledPageTitle>
                <Box height={'20px'}/>
                <ContentBody>
                    {this.renderOverview()}
                    {this.renderTopicCategory()}
                    {this.renderTopicList()}
                </ContentBody>
                <BlockSpacer height={'15px'}/>
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentDisasterTopicListPage)

const ContentBody = styled.div`
    max-width: 738px;
    margin: auto;
`

const HistoryLink = styled(PlainLink)`
    display: inline-block;
    text-align: right;
    margin: 0;
`

const StyledCardActionArea = withStyles({
    root: {
        borderBottom: `1px solid ${Colors.AccentLight}`
    },
})(CardActionArea)

const TopicArea = styled.div`
    width: 738px;
    background-color: ${Colors.Surface};
    margin: auto;
    ${currentMedia.lessThan('sp')`
        width: calc(100% + 30px);
        margin: 0px -15px;
  `};
`

const CreateTopicContainer = styled.div`
    display: flex;
    margin-left: 15px;
    color: ${Colors.Primary};
    gap: 15px;
    justify-content: unset;
    text-align: start;
    align-items: center;
    height: 67px;
`

const CreateTopicIcon = styled.img`
    width: 20px;
    height: 20px;
`

const TopicContainer: StyledDiv = styled.div`
    padding: 15px 15px;
    position: relative;
    display: flex;
    line-height: 18px;
`

const TopicIndex = styled.div<{ pastFlag?: boolean }>`
    width: 5px;
    border-radius: 3px;
    background-color: ${Colors.Disaster};
    background-color: ${props => props.pastFlag ? Colors.AccentLight : Colors.Disaster};
`

const TopicHeader: StyledDiv = styled.div`
    display: flex;
    align-items: center;
    font-size: ${FontSize.Small};
    color: ${Colors.Body};
`

const CategoryLabel = styled.div`
    background-color: ${Colors.Background};
    text-align: center;
    padding: 3px 10px;
`

const TopicTitle: StyledDiv = styled.div`
    margin-top: 5px;
    font-size: ${FontSize.Body};
    color: ${Colors.AccentDark};
    line-height: 24px;
    font-weight: ${FontWeights.Bold};
`

const TopicCommentContainer: StyledDiv = styled.div`
    display: flex;
    flex-grow: 1;
    align-items: center;
    position: relative;
    font-weight: ${FontWeights.Normal};
    font-size: ${FontSize.Small};
    line-height: 18px;
    color: ${Colors.Body};
    white-space: nowrap;
`

const TopicComment: StyledDiv = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 5px;
`

const CommentCountContainer: StyledDiv = styled.div`
    display: flex;
    font-size: ${FontSize.Middle};
    line-height: 21px;
    color: ${Colors.Body};
    justify-content: flex-end;
    padding-bottom: 5px;
    padding-top: 5px;
`

const IconWithCircleStyle = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    font-size: 13px;
    color: #BDBDBD;

    background: #FFFFFF;
    border: 1px solid #D9D9D9;
    box-sizing: border-box;
    border-radius: 50%;
`